import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { notificationsApi, withOpcoParam } from '../services/api'
import { getWorkspaceCode } from '../config/opcoWorkspace'
import { useAuth } from '../context/AuthContext'
import { parseUTC } from '../utils/dateUtils'
import { Bell, CheckCheck, AlertTriangle, ExternalLink, X, Trash2, Loader2 } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

// Inline base64-encoded short notification sound (a gentle "ding")
const NOTIF_SOUND_URL = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAABhsr+OQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA'

const PAGE_LIMIT = 50

// Named events that end the notification stream (backend opco/stream_guard.py).
export const STREAM_END_EVENT = 'stream_end'
export const SCOPE_REVOKED_EVENT = 'scope_revoked'

// SSE URL for the notification stream. EventSource cannot send headers, so
// the OpCo workspace rides along as ?opco=<code> (a no-op with the flag off).
export function buildNotificationStreamUrl(streamToken) {
  // Resolve a relative API_BASE against the current origin
  const base = API_BASE.startsWith('http')
    ? API_BASE
    : `${window.location.origin}${API_BASE}`
  return withOpcoParam(`${base}/notifications/stream?token=${encodeURIComponent(streamToken)}`)
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [toast, setToast] = useState(null)
  const [filter, setFilter] = useState('all') // 'all' | 'unread'
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [announcement, setAnnouncement] = useState('')
  const [removingIds, setRemovingIds] = useState(() => new Set())
  const lastCountRef = useRef(-1)
  const ref = useRef(null)
  const navigate = useNavigate()
  const sseDisabledRef = useRef(false)
  const { isAuthenticated, accessToken } = useAuth()

  // Fetch unread count via REST (used as initial load + fallback).
  // Returns false when the session is gone, so timer-driven callers can stop
  // instead of re-issuing a request that can only ever fail.
  const fetchCount = useCallback(async () => {
    try {
      const { data } = await notificationsApi.unreadCount()
      const newCount = data.unread_count || 0
      lastCountRef.current = newCount
      setUnreadCount(newCount)
      return true
    } catch (err) {
      // A 401 is not a transient blip — the token is gone or rejected, and
      // the API layer has already dispatched session-expired. Swallowing it
      // as "silent" is what turned an expired session into an unread-count
      // request every 30 seconds, forever, each one logged as a 401.
      return err?.response?.status !== 401
    }
  }, [])

  // Full fetch (for when panel is open). Resets to page 1 for the given filter.
  const fetchAll = useCallback(async (unreadOnly) => {
    try {
      const { data } = await notificationsApi.list(unreadOnly, { limit: PAGE_LIMIT, offset: 0 })
      const list = data.notifications || []
      setNotifications(list)
      setUnreadCount(data.unread_count || 0)
      setHasMore(!!data.has_more)
      setOffset(list.length)
      setAnnouncement(`${unreadOnly ? 'Unread' : 'All'} notifications, ${list.length} result${list.length === 1 ? '' : 's'}`)
    } catch { /* silent */ }
  }, [])

  const loadMore = useCallback(async () => {
    setLoadingMore(true)
    try {
      const unreadOnly = filter === 'unread'
      const { data } = await notificationsApi.list(unreadOnly, { limit: PAGE_LIMIT, offset })
      const nextPage = data.notifications || []
      setNotifications(prev => [...prev, ...nextPage])
      setUnreadCount(data.unread_count || 0)
      setHasMore(!!data.has_more)
      setOffset(prev => prev + nextPage.length)
    } catch { /* silent */ } finally {
      setLoadingMore(false)
    }
  }, [filter, offset])

  // ── SSE connection ────────────────────────────────────────────────
  useEffect(() => {
    // Nothing here can succeed without a session, and every attempt costs a
    // rejected request. The bell renders inside the authenticated Layout, but
    // "mounted" and "has a usable token" are not the same thing: the token is
    // cleared on session expiry a moment before the redirect to /login
    // unmounts us, and MSAL can report an account while the API token is
    // still null.
    if (!isAuthenticated || !accessToken) return

    // A fresh session deserves a fresh attempt at SSE, even if the previous
    // one gave up and fell back to polling.
    sseDisabledRef.current = false

    let es = null
    let reconnectTimer = null
    let pollingTimer = null
    let reconnectAttempts = 0
    let sessionLost = false

    const stopPolling = () => {
      if (pollingTimer) { clearInterval(pollingTimer); pollingTimer = null }
    }

    // The session is gone: tear everything down. The API layer has already
    // dispatched session-expired, so the app is on its way to /login — there
    // is nothing left for this component to poll for.
    const abandon = () => {
      sessionLost = true
      stopPolling()
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null }
      if (es) { es.close(); es = null }
    }

    const refreshCount = async () => {
      if (sessionLost) return
      if (!(await fetchCount())) abandon()
    }

    refreshCount()

    const startPollingFallback = () => {
      if (pollingTimer || sessionLost) return
      pollingTimer = setInterval(refreshCount, 30000)
    }

    const connect = async () => {
      if (sessionLost) return
      if (sseDisabledRef.current) {
        startPollingFallback()
        return
      }

      let streamToken = null
      try {
        const { data } = await notificationsApi.streamTicket()
        streamToken = data?.token
      } catch (err) {
        // 401 here means the same thing it means anywhere else — no amount of
        // retrying will produce a ticket without a session.
        if (err?.response?.status === 401) { abandon(); return }
        reconnectAttempts += 1
        if (reconnectAttempts >= 8) {
          sseDisabledRef.current = true
          startPollingFallback()
          return
        }
        const delayMs = Math.min(30000, 2000 * reconnectAttempts)
        reconnectTimer = setTimeout(() => { connect() }, delayMs)
        return
      }
      if (!streamToken || sessionLost) return

      es = new EventSource(buildNotificationStreamUrl(streamToken))

      es.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data)
          const newCount = payload.unread_count ?? 0
          reconnectAttempts = 0

          // Detect new notification(s)
          if (newCount > lastCountRef.current && lastCountRef.current >= 0) {
            // Play sound
            try {
              const audio = new Audio(NOTIF_SOUND_URL)
              audio.volume = 0.5
              audio.play().catch(() => {})
            } catch { /* silent */ }

            // Show toast from latest notification pushed by server
            const latest = payload.latest
            if (latest && !latest.is_read) {
              setToast({ title: latest.title, message: latest.message, link: latest.link, id: latest.id })
              setTimeout(() => setToast(null), 15000)
            }
          }

          lastCountRef.current = newCount
          setUnreadCount(newCount)
        } catch { /* silent */ }
      }

      // OpCo workspaces: the server ends the stream with a named event.
      // stream_end = it reached its maximum lifetime; reconnect straight away
      // with a fresh ticket (not a failure, so it does not count as a retry).
      // scope_revoked = the user lost access to this workspace; do not
      // reconnect (that would only be refused) and let the shell re-evaluate
      // the workspace, as for any OPCO_FORBIDDEN answer.
      es.addEventListener(STREAM_END_EVENT, () => {
        if (es) { es.close(); es = null }
        if (sessionLost) return
        reconnectAttempts = 0
        reconnectTimer = setTimeout(() => { connect() }, 1000)
      })
      es.addEventListener(SCOPE_REVOKED_EVENT, () => {
        abandon()
        window.dispatchEvent(new CustomEvent('opco-forbidden', {
          detail: { url: '/notifications/stream', workspace: getWorkspaceCode() },
        }))
      })

      es.onerror = () => {
        // EventSource auto-reconnects, but if it closes we schedule
        // a manual reconnect with a fresh token after a short delay.
        if (es) { es.close(); es = null }
        if (sessionLost) return

        reconnectAttempts += 1
        if (reconnectAttempts >= 8) {
          // Stop hammering the API if stream keeps failing (e.g. 400/401/network).
          sseDisabledRef.current = true
          startPollingFallback()
          return
        }

        const delayMs = Math.min(30000, 2000 * reconnectAttempts)
        reconnectTimer = setTimeout(() => { connect() }, delayMs)
      }
    }

    connect()

    // Also refresh on window focus as a safety net
    const onFocus = () => { refreshCount() }
    window.addEventListener('focus', onFocus)

    return () => {
      sessionLost = true
      if (es) { es.close(); es = null }
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (pollingTimer) clearInterval(pollingTimer)
      window.removeEventListener('focus', onFocus)
    }
  }, [fetchCount, isAuthenticated, accessToken])

  // Fetch full list when opening dropdown
  const handleOpen = async () => {
    if (open) { setOpen(false); return }
    setOpen(true)
    setLoading(true)
    await fetchAll(filter === 'unread')
    setLoading(false)
  }

  // Toggle Unread/All filter - immediate re-fetch, no reload
  const handleFilterChange = async (nextFilter) => {
    if (nextFilter === filter) return
    setFilter(nextFilter)
    setLoading(true)
    await fetchAll(nextFilter === 'unread')
    setLoading(false)
  }

  // Click outside to close
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const markRead = async (id) => {
    try {
      await notificationsApi.markRead(id)
      setUnreadCount(prev => Math.max(0, prev - 1))
      if (filter === 'unread') {
        // Fade + collapse, then remove from the filtered (unread) list
        setRemovingIds(prev => new Set(prev).add(id))
        setTimeout(() => {
          setNotifications(prev => prev.filter(n => n.id !== id))
          // Keep the pagination cursor in sync: one fewer item is now
          // loaded locally, so the next "load more" fetch must start one
          // position earlier to avoid skipping a notification.
          setOffset(prev => Math.max(0, prev - 1))
          setRemovingIds(prev => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        }, 200)
      } else {
        // "All" mode: restyle in place
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
      }
    } catch { /* silent */ }
  }

  const markAllRead = async () => {
    try {
      await notificationsApi.markAllRead()
      if (filter === 'unread') {
        // Every currently-unread notification just became read, so under
        // the Unread filter the list is now genuinely empty server-side too.
        // Reset the pagination cursor so no stale "Load more" button remains.
        setNotifications([])
        setOffset(0)
        setHasMore(false)
      } else {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      }
      setUnreadCount(0)
    } catch { /* silent */ }
  }

  const dismissOne = async (e, id) => {
    e.stopPropagation()
    try {
      await notificationsApi.dismiss(id)
      setNotifications(prev => prev.filter(n => n.id !== id))
      // One fewer item is now loaded locally; keep the offset in sync so the
      // next "load more" fetch doesn't skip a notification.
      setOffset(prev => Math.max(0, prev - 1))
      // Refresh unread count
      fetchCount()
    } catch { /* silent */ }
  }

  const clearAll = async () => {
    try {
      await notificationsApi.clearAll()
      // All notifications are gone server-side, so reset the pagination
      // cursor too — otherwise a stale "Load more" button could remain.
      setNotifications([])
      setOffset(0)
      setHasMore(false)
      setUnreadCount(0)
    } catch { /* silent */ }
  }

  const handleClick = (n) => {
    if (!n.is_read) markRead(n.id)
    if (n.link) { navigate(n.link); setOpen(false) }
  }

  const timeAgo = (iso) => {
    if (!iso) return ''
    const parsed = parseUTC(iso)
    if (!parsed || isNaN(parsed)) return ''
    const s = Math.floor((Date.now() - parsed.getTime()) / 1000)
    if (s < 60) return 'just now'
    if (s < 3600) return Math.floor(s / 60) + 'm ago'
    if (s < 86400) return Math.floor(s / 3600) + 'h ago'
    return Math.floor(s / 86400) + 'd ago'
  }

  // Group notifications into Today / Earlier buckets (data already sorted newest-first)
  const isToday = (iso) => {
    const parsed = iso ? parseUTC(iso) : null
    if (!parsed || isNaN(parsed)) return false
    const now = new Date()
    return parsed.getFullYear() === now.getFullYear() &&
      parsed.getMonth() === now.getMonth() &&
      parsed.getDate() === now.getDate()
  }
  const todayNotifications = notifications.filter(n => isToday(n.created_at))
  const earlierNotifications = notifications.filter(n => !isToday(n.created_at))

  const renderNotification = (n) => (
    <div
      key={n.id}
      className={`relative group w-full text-left px-4 py-3 border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer overflow-hidden ${
        !n.is_read ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''
      } ${
        removingIds.has(n.id)
          ? 'opacity-0 max-h-0 !py-0 !border-b-0'
          : 'opacity-100 max-h-40'
      }`}
      style={{ transition: 'opacity 200ms ease, max-height 200ms ease, padding 200ms ease' }}
    >
      {/* Dismiss X button */}
      <button
        onClick={(e) => dismissOne(e, n.id)}
        className="absolute top-2 right-2 p-1 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all z-10"
        title="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <div onClick={() => handleClick(n)} className="flex items-start gap-3">
        <div className={`mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          n.type === 'extraction_failed'
            ? 'bg-amber-100 dark:bg-amber-900/30'
            : 'bg-blue-100 dark:bg-blue-900/30'
        }`}>
          <AlertTriangle className={`h-4 w-4 ${
            n.type === 'extraction_failed'
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-blue-600 dark:text-blue-400'
          }`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm truncate ${!n.is_read ? 'font-semibold text-gray-900 dark:text-gray-100' : 'font-medium text-gray-700 dark:text-gray-300'}`}>
              {n.title}
            </p>
            {!n.is_read && <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full" />}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{n.message}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-gray-400 dark:text-gray-500">{timeAgo(n.created_at)}</span>
            {n.link && <ExternalLink className="h-2.5 w-2.5 text-gray-400" />}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        title="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-sm">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] sm:max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAll}
                  className="text-xs text-red-500 dark:text-red-400 hover:underline flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Clear all
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Unread / All filter toggle */}
          <div
            role="tablist"
            aria-label="Filter notifications"
            className="flex items-center gap-1 px-4 pt-2 pb-1 border-b border-gray-100 dark:border-gray-800"
          >
            {[
              { key: 'all', label: 'All' },
              { key: 'unread', label: 'Unread' },
            ].map(tab => (
              <button
                key={tab.key}
                role="tab"
                type="button"
                aria-selected={filter === tab.key}
                tabIndex={0}
                onClick={() => handleFilterChange(tab.key)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleFilterChange(tab.key)
                  }
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900 transition-colors ${
                  filter === tab.key
                    ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
            {/* Live region announcing filter + result count for screen readers */}
            <span aria-live="polite" className="sr-only">{announcement}</span>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {filter === 'unread' ? "You're all caught up!" : 'No notifications yet'}
                </p>
              </div>
            ) : (
              <>
                {todayNotifications.length > 0 && (
                  <>
                    <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      Today
                    </div>
                    {todayNotifications.map(renderNotification)}
                  </>
                )}
                {earlierNotifications.length > 0 && (
                  <>
                    <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      Earlier
                    </div>
                    {earlierNotifications.map(renderNotification)}
                  </>
                )}
                {hasMore && (
                  <div className="px-4 py-3 text-center">
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingMore ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Toast popup for new notifications - rendered via portal to document.body */}
      {toast && ReactDOM.createPortal(
        <div
          className="fixed top-4 right-4 z-[9999] w-96 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl p-4 cursor-pointer"
          onClick={() => { setToast(null); if (toast.link) navigate(toast.link) }}
          style={{ animation: 'notifSlideIn 0.3s ease-out, notifFadeOut 0.5s ease-in 4.5s forwards' }}
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{toast.title}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{toast.message}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); setToast(null) }} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes notifSlideIn { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes notifFadeOut { from { opacity: 1; } to { opacity: 0; pointer-events: none; } }
      `}</style>
    </div>
  )
}
